import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../services/API/api.service';
import { AlertService } from '../../services/Alert/alert.service';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss']
})
export class CartComponent implements OnInit {
  cartItems: any[] = [];
  cartCustomProducts: any[] = [];
  cartItemsLength: number = 0;
  subtotal: number = 0;
  total: number = 0;
  paymentMethod: string = 'card';
  isLoading: boolean = false;
  guestMode: boolean = false;
  
  couponApplied: boolean = false;
  couponType: string = 'percentage';
  couponValue: number = 0;
  couponCode: string = '';
  couponCodeForm: FormGroup

  constructor(
    private apiService: ApiService,
    private alertService: AlertService,
    private router: Router,
    private fb: FormBuilder
  ) {
    this.couponCodeForm = this.fb.group({
      code: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    const guestMode = sessionStorage.getItem('guestMode');
    const token = localStorage.getItem('romaPizza_token')?.toString();
    const couponApplied = localStorage.getItem('couponApplied');
    
    if(token){
      this.getCartItems();
      this.getCartCustomProducts();
    }else if(guestMode){
      this.guestMode = true;
      this.loadGuestCart();
      this.loadGuestCustomCart();
    }else{
      this.alertService.danger('You must be logged in or continue as a guest to view your cart');
    }

    if(couponApplied){
      this.couponApplied = true;
      this.couponValue = parseFloat(localStorage.getItem('couponValue'));
      this.couponCode = localStorage.getItem('couponCode');
      this.couponType = localStorage.getItem('couponType');
      this.calculateTotals();
    }

    localStorage.setItem('paymentMethod', this.paymentMethod);
  }

  changePayment(payment: string){
    this.paymentMethod = payment;
    localStorage.setItem('paymentMethod', this.paymentMethod);
  }

  loadGuestCart() {
    this.isLoading = true;
    const cartItems = sessionStorage.getItem('cartItems');
    if (cartItems) {
      this.cartItems = JSON.parse(cartItems);
      
      this.cartItems = this.cartItems.map(item => {
        if (!item.selectedTypeName && item.type_name) {
          item.selectedTypeName = item.type_name;
        }
        if (!item.selectedExtraId && item.extra_ids) {
          item.selectedExtraId = item.extra_ids;
        }
        return item;
      });
      
      this.cartItemsLength = this.cartItems.length + this.cartCustomProducts.length;
      this.calculateTotals();
    }
    this.isLoading = false;
  }

  loadGuestCustomCart() {
    const customCartItems = sessionStorage.getItem('customCartItems');
    if (customCartItems) {
      this.cartCustomProducts = JSON.parse(customCartItems);
      this.cartItemsLength = this.cartItems.length + this.cartCustomProducts.length;
      this.calculateTotals();
    }
  }

  getCartItems() {
    this.isLoading = true;
    this.apiService.getCart().subscribe({
      next: response => {
        this.cartItems = response.data.data;
        this.cartItemsLength = this.cartItems.length + this.cartCustomProducts.length;
        this.calculateTotals();
        this.isLoading = false;
      }, 
      error: (error: any) => {
        this.alertService.danger(error.error?.message || 'Failed to load cart items');
        this.isLoading = false;
      }
    });
  }

  getCartCustomProducts() {
    this.apiService.getCartCustomProducts().subscribe({
      next: (response: any) => {
        if (response.success && response.data?.data) {
          this.cartCustomProducts = response.data.data;
          this.cartItemsLength = this.cartItems.length + this.cartCustomProducts.length;
          this.calculateTotals();
        }
      }, 
      error: (error: any) => {
        console.error('Error loading custom products:', error);
      }
    });
  }

  saveGuestCart() {
    const guestCart = this.cartItems.map(item => ({
        id: item.id || item.product_id,
        name: item.name,
        avatar: item.avatar,
        offer: item.offer || null,
        rate: item.rate || 0,
        components: item.components,
        types: item.types,
        extraproducts: item.extraproducts || [],
        selectedTypeId: item.selectedTypeId,
        selectedExtraId: item.selectedExtraId || item.selectedExtraIds || [],
        quantity: item.quantity,
        category_name: item.category_name || item.categoryName || 'Menu',
        price: item.price || this.getOriginalItemPrice(item),
        side: item.side || [],
        free_side_count: item.free_side_count || item.free_side || 0
    }));
    
    sessionStorage.setItem('cartItems', JSON.stringify(guestCart));
  }

  saveGuestCustomCart() {
    sessionStorage.setItem('customCartItems', JSON.stringify(this.cartCustomProducts));
  }

  refreshCartItems() {
    const guestMode = sessionStorage.getItem('guestMode');
    const token = localStorage.getItem('romaPizza_token')?.toString();
    
    if(token){
      this.getCartItems();
      this.getCartCustomProducts();
    }else if(guestMode){
      this.loadGuestCart();
      this.loadGuestCustomCart();
    }else{
      this.alertService.danger('You must be logged in or continue as a guest to view your cart');
    }
  }

  // Helper methods for displaying custom products
  getCustomProductName(customItem: any): string {
    if (!customItem || !customItem.customProducts) return 'Custom Product';
    
    const customProducts = customItem.customProducts;
    const categoryName = customProducts.category?.name || '';
    const subCategoryName = customProducts.subCategory?.name || '';
    
    if (categoryName && subCategoryName) {
      return `Custom ${categoryName} - ${subCategoryName}`;
    } else if (categoryName) {
      return `Custom ${categoryName}`;
    }
    return 'Custom Product';
  }

  getCustomProductImage(customItem: any): string {
    if (!customItem || !customItem.customProducts) return 'assets/images/default-food.png';
    
    const customProducts = customItem.customProducts;
    return  customProducts.category?.avatar || 
            customProducts.subCategory?.avatar || 
            'assets/images/default-food.png';
  }

  getCustomProductSize(customItem: any): string {
    if (!customItem || !customItem.customProducts) return '';
    
    const customProducts = customItem.customProducts;
    if (customProducts.category?.selected_size) {
      return customProducts.category.selected_size.name;
    } else if (customProducts.category?.has_sizes === 0) {
      return 'Regular';
    }
    return '';
  }

  getCustomProductPrice(customItem: any): number {
    if (!customItem || !customItem.customProducts) return 0;
    return customItem.customProducts.totalPrice || 0;
  }

  getCustomProductTotalPrice(customItem: any): number {
    if (!customItem) return 0;
    const price = this.getCustomProductPrice(customItem);
    return price * (customItem.quantity || 1);
  }

  isHalfAndHalf(customItem: any): boolean {
    return customItem?.customProducts?.isHalfAndHalf || false;
  }

  getLeftHalfIngredients(customItem: any): any[] {
    if (!customItem || !customItem.customProducts || !customItem.customProducts.ingredients) {
      return [];
    }
    return customItem.customProducts.ingredients.filter(
      (ing: any) => ing.half === 'left' || ing.half === 'whole'
    );
  }

  getRightHalfIngredients(customItem: any): any[] {
    if (!customItem || !customItem.customProducts || !customItem.customProducts.ingredients) {
      return [];
    }
    return customItem.customProducts.ingredients.filter(
      (ing: any) => ing.half === 'right' || ing.half === 'whole'
    );
  }

  getAllIngredients(customItem: any): any[] {
    return customItem?.customProducts?.ingredients || [];
  }

  formatIngredientDisplay(ingredient: any): string {
    if (!ingredient) return '';
    
    let display = ingredient.name || 'Ingredient';
    if (ingredient.quantity && ingredient.quantity > 1) {
      display += ` (${ingredient.quantity}x)`;
    }
    if (ingredient.half && ingredient.half !== 'whole') {
      display += ` - ${ingredient.half} half`;
    }
    return display;
  }

  // Regular cart item methods
  getUniqueExtrasWithCount(item: any): any[] {
    if (!item || !item.selectedExtraId || !Array.isArray(item.selectedExtraId)) {
        return [];
    }
    
    const countMap = new Map<number, number>();
    item.selectedExtraId.forEach((id: number) => {
        countMap.set(id, (countMap.get(id) || 0) + 1);
    });
    
    const uniqueExtras: any[] = [];
    countMap.forEach((count, id) => {
        const extraInfo = this.getExtraInfo(item, id);
        if (extraInfo) {
            uniqueExtras.push({
                id: id,
                name: extraInfo.name,
                count: count
            });
        }
    });
    
    return uniqueExtras;
  }

  getExtraInfo(item: any, extraId: number): any {
    if (!item || !item.extraproducts || !Array.isArray(item.extraproducts)) {
        return null;
    }
    return item.extraproducts.find((e: any) => e.id == extraId) || null;
  }

  getExtraName(item: any, extraId: number): string {
    try {
        if (item && item.selectedExtraId && Array.isArray(item.selectedExtraId)) {
            const count = item.selectedExtraId.filter((id: number) => id === extraId).length;
            
            if (item.extraproducts && Array.isArray(item.extraproducts)) {
                const extra = item.extraproducts.find((e: any) => e.id == extraId);
                if (extra && extra.name) {
                    if (count > 1) {
                        return `${extra.name} (${count}x)`;
                    }
                    return extra.name;
                }
            }
        }
        return `Extra #${extraId}`;
    } catch (error) {
        console.error('Error getting extra name:', error);
        return 'Extra';
    }
  }

  updateCartItem(index: number) {
    const guestMode = sessionStorage.getItem('guestMode');
    const token = localStorage.getItem('romaPizza_token')?.toString();
    const item = this.cartItems[index];
    
    let extras: any[] = [];
    if (item.selectedExtraId && Array.isArray(item.selectedExtraId) && item.selectedExtraId.length > 0) {
        extras = item.extraproducts.filter((extra: any) => 
            extra && item.selectedExtraId.some((selectedExtra: any) => 
                selectedExtra && selectedExtra.id === extra.id
            )
        );
    } else {
        extras = [];
    }
    
    const freeSideIds = item.selectedFreeSides && Array.isArray(item.selectedFreeSides) 
        ? item.selectedFreeSides
            .filter((side: any) => side && side.id)
            .map((side: any) => side.id)
        : [];
    
    const extraIds = item.selectedExtraId && Array.isArray(item.selectedExtraId)
        ? item.selectedExtraId
            .filter((extra: any) => extra && extra.id)
            .map((extra: any) => extra.id)
        : [];
    
    const body = {
        products: [
            {
                product_id: item.id,
                type: item.selectedTypeId,
                quantity: item.quantity,
                extra: extraIds,
                side: freeSideIds
            }
        ]
    };

    if (token) {
        this.apiService.addToCart(body).subscribe({
            next: () => {
                this.calculateTotals();
                this.refreshCartItems();
                this.alertService.success('Cart updated successfully');
            },
            error: (error) => {
                this.alertService.danger(error.error?.message || 'Failed to update cart');
                this.refreshCartItems();
            }
        });
    } else if (guestMode) {
        this.saveGuestCart();
        this.calculateTotals();
    } else {
        this.alertService.danger('You must be logged in or continue as a guest to view your cart');
    }
  }

  increaseQuantity(index: number) {
    this.cartItems[index].quantity++;
    const guestMode = sessionStorage.getItem('guestMode');
    if(guestMode){
      this.saveGuestCart();
      this.calculateTotals();
      this.alertService.success('Cart updated');
    }
    this.updateCartItem(index);
  }

  decreaseQuantity(index: number) {
    if (this.cartItems[index].quantity > 1) {
      this.cartItems[index].quantity--;
      const guestMode = sessionStorage.getItem('guestMode');
      if(guestMode){
        this.saveGuestCart();
        this.calculateTotals();
        this.alertService.success('Cart updated');
      }
      this.updateCartItem(index);
    } else {
      if (confirm('Remove this item from cart?')) {
        this.removeItem(index);
      } else {
        this.cartItems[index].quantity = 1;
      }
    }
  }

  removeItem(index: number) {
    const itemId = this.cartItems[index].cart_id;
    const guestMode = sessionStorage.getItem('guestMode');
    const token = localStorage.getItem('romaPizza_token')?.toString();
    
    if(token){
      this.apiService.removeCartItem(itemId).subscribe({
        next: () => {
          this.cartItems.splice(index, 1);
          this.cartItemsLength = this.cartItems.length + this.cartCustomProducts.length;
          this.getCartItems();
          this.calculateTotals();
          this.alertService.success('Item removed from cart');
        },
        error: (error) => {
          this.alertService.danger(error.error?.message || 'Failed to remove item');
        }
      });
    }else if(guestMode){
      this.cartItems.splice(index, 1);
      this.cartItemsLength = this.cartItems.length + this.cartCustomProducts.length;
      this.saveGuestCart();
      this.calculateTotals();
      this.alertService.success('Item removed from cart');
    }else{
      this.alertService.danger('You must be logged in or continue as a guest to view your cart');
    }
  }

  // Custom product removal (keep existing functionality)
  removeCustomItem(index: number) {
    const itemId = this.cartCustomProducts[index]?.id;
    console.log('Removing custom item with ID:', itemId);
    const guestMode = sessionStorage.getItem('guestMode');
    const token = localStorage.getItem('romaPizza_token')?.toString();
    
    if(token && itemId) {
      this.apiService.removeCustomCartItem(itemId).subscribe({
        next: () => {
          this.cartCustomProducts.splice(index, 1);
          this.cartItemsLength = this.cartItems.length + this.cartCustomProducts.length;
          this.calculateTotals();
          this.alertService.success('Item removed from cart');
        },
        error: (error) => {
          this.alertService.danger(error.error?.message || 'Failed to remove item');
        }
      });
    } else if (guestMode) {
      this.cartCustomProducts.splice(index, 1);
      this.cartItemsLength = this.cartItems.length + this.cartCustomProducts.length;
      this.saveGuestCustomCart();
      this.calculateTotals();
      this.alertService.success('Item removed from cart');
    }
  }

  getOriginalItemPrice(item: any): number {
    if (!item.selectedTypeId && item.types?.length) {
        return item.types[0].price;
    }
    
    const selectedType = item.types?.find(t => t.id === item.selectedTypeId);
    return selectedType?.price || item.price || 0;
  }

  getItemPrice(item: any): number {
    const basePrice = this.getOriginalItemPrice(item);
    
    let extrasPrice = 0;
    if (item.selectedExtraId && Array.isArray(item.selectedExtraId)) {
        extrasPrice = item.selectedExtraId.reduce((total: number, extra: any) => {
            const extraPrice = extra?.price || 0;
            const extraQuantity = extra?.quantity || 0;
            return total + (extraPrice * extraQuantity);
        }, 0);
    }
    
    let freeSidesPrice = 0;
    if (item.selectedFreeSides && Array.isArray(item.selectedFreeSides)) {
        freeSidesPrice = item.selectedFreeSides.reduce((total: number, side: any) => {
            return total + (side?.price || 0);
        }, 0);
    }
    
    const totalBasePrice = basePrice + extrasPrice + freeSidesPrice;
    
    if (!item.offer) return totalBasePrice;
    
    if (item.offer.type === 'discount') {
        return Math.max(0, totalBasePrice - item.offer.value);
    } 
    else if (item.offer.type === 'percentage') {
        return totalBasePrice * (1 - item.offer.value / 100);
    }
    
    return totalBasePrice;
  }

  getItemTotalPrice(item: any): number {
    return this.getItemPrice(item) * item.quantity;
  }

  getItemDiscountText(item: any): string {
    if (!item.offer) return '';
    
    if (item.offer.type === 'discount') {
        return `Save $${item.offer.value}`;
    }
    else if (item.offer.type === 'percentage') {
        return `${item.offer.value}% off`;
    }
    
    return '';
  }

  cartDiscount: any

  calculateDiscountedPrice2(item: any, price: number): number {
      if (!item.offer) return price;
      
      if (item.offer.type === 'discount') {
          return Math.max(0, price - item.offer.value);
      } 
      else if (item.offer.type === 'percentage') {
          return price * (1 - item.offer.value / 100);
      }
      
      return price;
  }

  calculateTotals() {
    let subtotal = 0;
    let cartDiscount = 0;
    
    // Calculate regular items totals
    this.cartItems.forEach(item => {
        const itemTotal = this.getItemTotalPrice(item);
        subtotal += itemTotal;
        
        const originalPrice = this.getOriginalItemPrice(item);
        const discountedPrice = this.getItemPrice(item);
        const originalTotal = originalPrice * item.quantity;
        const discountedTotal = discountedPrice * item.quantity;
        cartDiscount += (originalTotal - discountedTotal);
    });
    
    // Calculate custom products totals
    this.cartCustomProducts.forEach(item => {
        const itemTotal = this.getCustomProductTotalPrice(item);
        subtotal += itemTotal;
    });
    
    this.subtotal = subtotal;
    this.cartDiscount = cartDiscount;
    
    // Calculate coupon discount
    let couponDiscount = 0;
    if (this.couponApplied) {
        couponDiscount = this.couponType === 'percentage'
            ? this.subtotal * (this.couponValue / 100)
            : Math.min(this.couponValue, this.subtotal);
    }
    
    // Calculate final total
    this.total = Math.max(0, this.subtotal - couponDiscount);
  }

  coponType: string = '';
  applyCoupon() {
    if (!this.couponCode) {
        this.alertService.danger('Please enter a coupon code');
        return;
    }

    const token = localStorage.getItem('romaPizza_token')?.toString();
    const guestMode = sessionStorage.getItem('guestMode');
    
    if (token || guestMode) {
        this.couponCodeForm.patchValue({ code: this.couponCode });
        this.apiService.applyCoupon(this.couponCodeForm.value).subscribe({
            next: (response) => {
                if (response.status) {
                    this.couponApplied = true;
                    this.couponType = response.copon_type;
                    this.couponValue = parseFloat(response.copon_value);
                    if(response.status == true){
                      this.alertService.success('Coupon applied successfully');
                      localStorage.setItem('couponApplied', 'true');
                      localStorage.setItem('couponId', response.copon_id);
                      localStorage.setItem('couponType', this.couponType);
                      localStorage.setItem('couponValue', this.couponValue.toString());
                      localStorage.setItem('couponCode', this.couponCode);
                      this.calculateTotals();
                    }
                } else {
                    this.alertService.danger('Invalid coupon code');
                }
            },
            error: (error) => {
                this.alertService.danger(error.error?.message || 'Failed to apply coupon');
            }
        });
    } else {
        this.alertService.danger('You must be logged in to apply a coupon');
    }
  }

  proceedToCheckout() {
    if (this.cartItemsLength === 0) {
      this.alertService.danger('Your cart is empty');
      return;
    }else{
      this.router.navigate(['/checkout']);
    }
  }

  removeCoupon(){
    localStorage.removeItem('couponApplied');
    localStorage.removeItem('couponValue');
    localStorage.removeItem('couponCode');
    localStorage.removeItem('couponType');
    localStorage.removeItem('couponId');
    this.couponCode = '';
    this.couponApplied = false;
    this.couponValue = 0;
    this.calculateTotals();
  }
}